import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import type {
  AdminMembersResponseDto,
  AdminStudentsResponseDto,
  FamilyAccountDto,
  FamilyAccountStatus,
  UpdateFamilyAccountRequestDto
} from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchAdminMembers } from "../../shared/api/adminDirectory";
import {
  fetchAdminStudents,
  fetchFamilyAccount,
  patchFamilyAccount
} from "../../shared/api/adminRoster";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { describeStatus } from "../../shared/lib/uiLabels";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin/students", label: "生徒 / 家族へ戻る" },
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホームへ戻る" }
];

type FamilyAccountEditFormState = {
  displayName: string;
  primaryContactPlatformUserId: string;
  status: FamilyAccountStatus;
};

function createFormState(familyAccount: FamilyAccountDto): FamilyAccountEditFormState {
  return {
    displayName: familyAccount.displayName,
    primaryContactPlatformUserId: familyAccount.primaryContactPlatformUserId ?? "",
    status: familyAccount.status
  };
}

function buildPatchRequest(form: FamilyAccountEditFormState): UpdateFamilyAccountRequestDto {
  return {
    displayName: form.displayName.trim(),
    primaryContactPlatformUserId: form.primaryContactPlatformUserId || null,
    status: form.status
  };
}

function getMemberDisplayName(member: AdminMembersResponseDto["items"][number]): string {
  return member.appNickname ?? member.lineDisplayNameSnapshot ?? member.platformUserId;
}

function formatDateTime(value: string): string {
  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(parsed);
}

export function AdminFamilyAccountDetailPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant", familyAccountId = "" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [familyAccount, setFamilyAccount] = useState<FamilyAccountDto | null>(null);
  const [students, setStudents] = useState<AdminStudentsResponseDto | null>(null);
  const [members, setMembers] = useState<AdminMembersResponseDto | null>(null);
  const [form, setForm] = useState<FamilyAccountEditFormState | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));

  const linkedStudents = useMemo(
    () => students?.items.filter((student) => student.familyAccountId === familyAccountId) ?? [],
    [familyAccountId, students]
  );

  async function refreshPage() {
    setLoadState("loading");
    setLoadError(null);

    try {
      const [nextFamilyAccount, nextStudents, nextMembers] = await Promise.all([
        fetchFamilyAccount(tenantSlug, familyAccountId),
        fetchAdminStudents(tenantSlug),
        fetchAdminMembers(tenantSlug)
      ]);

      setFamilyAccount(nextFamilyAccount);
      setStudents(nextStudents);
      setMembers(nextMembers);
      setForm(createFormState(nextFamilyAccount));
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, "家族アカウント詳細の取得に失敗しました"));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole || !familyAccountId) {
      return;
    }

    void refreshPage();
  }, [familyAccountId, hasAdminRole, isSessionReady, tenantSlug]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!form) {
      return;
    }

    setBusy(true);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await patchFamilyAccount(tenantSlug, familyAccountId, buildPatchRequest(form));
      await refreshPage();
      setFeedbackMessage("家族アカウントを更新しました。");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "家族アカウントの更新に失敗しました"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="家族アカウント詳細"
      description="家族アカウントは兄弟姉妹のグループ単位です。保護者の閲覧権限そのものは保護者連携情報で管理します。"
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>家族アカウント詳細を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}

        {isSessionReady && hasAdminRole && familyAccount && form ? (
          <>
            <section
              style={{
                display: "grid",
                gap: 8,
                padding: 12,
                border: "1px solid #e5e5e5",
                borderRadius: 12
              }}
            >
              <h2 style={{ margin: 0, fontSize: 18 }}>読み取り専用情報</h2>
              <div>家族アカウントID: {familyAccount.familyAccountId}</div>
              <div>作成日時: {formatDateTime(familyAccount.createdAt)}</div>
              <div>更新日時: {formatDateTime(familyAccount.updatedAt)}</div>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>家族アカウント編集</h2>
              <form
                onSubmit={(event) => void handleSubmit(event)}
                style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
              >
                <label style={{ display: "grid", gap: 4 }}>
                  <span>家族名</span>
                  <input
                    value={form.displayName}
                    onChange={(event) => setForm((current) => (current ? { ...current, displayName: event.target.value } : current))}
                  />
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>代表保護者</span>
                  <select
                    value={form.primaryContactPlatformUserId}
                    onChange={(event) =>
                      setForm((current) =>
                        current ? { ...current, primaryContactPlatformUserId: event.target.value } : current
                      )
                    }
                  >
                    <option value="">未設定</option>
                    {members?.items.map((member) => (
                      <option key={member.platformUserId} value={member.platformUserId}>
                        {getMemberDisplayName(member)} / {member.platformUserId}
                      </option>
                    ))}
                  </select>
                  <span style={{ fontSize: 12, opacity: 0.8 }}>
                    代表保護者は任意です。変更しても guardian link は自動で解除されません。
                  </span>
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>ステータス</span>
                  <select
                    value={form.status}
                    onChange={(event) =>
                      setForm((current) =>
                        current
                          ? {
                              ...current,
                              status: event.target.value === "inactive" ? "inactive" : "active"
                            }
                          : current
                      )
                    }
                  >
                    <option value="active">{describeStatus("active")}</option>
                    <option value="inactive">{describeStatus("inactive")}</option>
                  </select>
                </label>

                <button type="submit" disabled={busy}>
                  {busy ? "更新中..." : "家族アカウントを更新"}
                </button>
              </form>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>紐付いている生徒</h2>
              {students ? null : <p style={{ margin: 0 }}>紐付いている生徒一覧は未接続です。</p>}
              {students && linkedStudents.length === 0 ? <p style={{ margin: 0 }}>紐付いている生徒はまだいません。</p> : null}
              {linkedStudents.length > 0 ? (
                <div style={{ display: "grid", gap: 12 }}>
                  {linkedStudents.map((student) => (
                    <Link
                      key={student.studentId}
                      to={`/t/${tenantSlug}/teacher/admin/students/${student.studentId}`}
                      style={{
                        display: "grid",
                        gap: 6,
                        padding: 12,
                        border: "1px solid #e5e5e5",
                        borderRadius: 12,
                        textDecoration: "none",
                        color: "inherit"
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                        <strong>{student.displayName}</strong>
                        <span style={{ fontSize: 12, opacity: 0.8 }}>{describeStatus(student.status)}</span>
                      </div>
                      <div style={{ fontSize: 14, opacity: 0.8 }}>
                        {student.memberCode} / {student.birthDate}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>{student.studentId}</div>
                    </Link>
                  ))}
                </div>
              ) : null}
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
